import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ROLE_LABELS, CHANNEL_LABELS, shortDate } from './constants';
import './Screens.css';

// The rolodex: every contact across every account, searchable, one tap to reach them.

const Contacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ search: '', role: '', channel: '', includeInactive: false });

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.role) params.append('role', filters.role);
      if (filters.channel) params.append('channel', filters.channel);
      if (filters.includeInactive) params.append('includeInactive', 'true');

      const response = await api.get(`/crm/contacts?${params.toString()}`);
      setContacts(response.data.contacts);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchContacts, filters.search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchContacts, filters.search]);

  const set = (key, value) => setFilters(p => ({ ...p, [key]: value }));

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div>
          <h1>Contacts</h1>
          <p className="subtitle">
            {contacts.length} {contacts.length === 1 ? 'person' : 'people'} across your accounts.
            Contacts are added from an account screen.
          </p>
        </div>
      </div>

      <div className="filters">
        <div className="grow">
          <input
            type="search"
            value={filters.search}
            onChange={(e) => set('search', e.target.value)}
            placeholder="Search name, email, or account…"
          />
        </div>
        <select value={filters.role} onChange={(e) => set('role', e.target.value)}>
          <option value="">All roles</option>
          {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.channel} onChange={(e) => set('channel', e.target.value)}>
          <option value="">Any channel</option>
          {Object.entries(CHANNEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button
          type="button"
          className={`btn${filters.includeInactive ? ' primary' : ''}`}
          onClick={() => set('includeInactive', !filters.includeInactive)}
        >
          {filters.includeInactive ? 'Showing departed' : 'Show departed'}
        </button>
      </div>

      {error && <p className="banner error">{error}</p>}
      {loading && contacts.length === 0 && <p className="loading">Loading…</p>}

      {!loading && contacts.length === 0 ? (
        <div className="card">
          <p className="empty">
            <strong>No contacts yet</strong>
            Coaches turn over constantly — the record surviving them is most of the point.
            Add people from an account screen.
          </p>
        </div>
      ) : (
        <ul className="crm-list">
          {contacts.map(c => {
            const phone = c.mobile || c.phone;
            return (
              <li key={c.id}>
                <div className="li-main">
                  <div className="li-title">
                    {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                    {c.is_primary && <span className="pill pay-paid" style={{ marginLeft: 8 }}>Primary</span>}
                    {!c.is_active && <span className="pill stage-skipped" style={{ marginLeft: 8 }}>Gone</span>}
                  </div>
                  <div className="li-sub">
                    {[c.title || ROLE_LABELS[c.role],
                      c.preferred_channel && c.preferred_channel !== 'unknown'
                        ? `prefers ${CHANNEL_LABELS[c.preferred_channel].toLowerCase()}` : null,
                      c.best_time,
                      c.last_contact_at ? `last talked ${shortDate(c.last_contact_at)}` : 'never contacted'
                    ].filter(Boolean).join(' · ')}
                  </div>
                  <button type="button" className="org-link" onClick={() => navigate(`/crm/organizations/${c.organization_id}`)}>
                    {c.organization_name}{c.city ? ` · ${c.city}` : ''}
                  </button>
                </div>
                <div className="li-actions">
                  {phone && <a className="tap" href={`tel:${phone}`}>Call</a>}
                  {phone && <a className="tap" href={`sms:${phone}`}>Text</a>}
                  {c.email && <a className="tap" href={`mailto:${c.email}`}>Email</a>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Contacts;
